import React, { useState, useContext, useRef } from "react";
import { Download, Upload, FileText, FileCheck, X, Info } from "lucide-react";
import { toast } from "react-toastify";
import { PostData } from "../../services/FetchData";
import "./ExportImport.scss";
import { ProductContext } from "../../store";

const ExportImport = ({ onClose }) => {
  const { currentWebsiteId } = useContext(ProductContext);
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importStatus, setImportStatus] = useState("");
  const [isError, setIsError] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportStatus("");
      setIsError(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportStatus("Please select a file first");
      setIsError(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);
    formData.append("websiteId", currentWebsiteId);

    try {
      setImportStatus("Importing...");
      setIsError(false);

      // Ensure we're sending the correct content-type header
      const result = await PostData(
        "ms1",
        "api/v1/synonyms/importSynonymsCSV",
        formData,
        true, // isFormData flag
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (result && result.status === "success") {
        setImportStatus("Import completed successfully!");
        setIsError(false);
        setImportFile(null);

        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = "";

        // Auto-dismiss success message after 4 seconds
        setTimeout(() => {
          setImportStatus("");
        }, 4000);

        toast.success("Import completed successfully!");
        onClose();
      } else {
        throw new Error(result?.message || "Failed to import synonyms");
      }
    } catch (error) {
      console.error("Import failed:", error);
      setImportStatus(
        error.message ||
          "Failed to import synonyms. Please check the file format and try again."
      );
      setIsError(true);
      toast.error(error.message || "Failed to import synonyms");
    }
  };

  const renderImportSection = () => (
    <div className="card">
      <div className="card-body">
        <div className="file-upload-section">
          <div
            className="file-upload-card compact"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add("drag-over");
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("drag-over");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("drag-over");
              if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                const file = e.dataTransfer.files[0];
                if (file.type === "text/csv" || file.name.endsWith(".csv")) {
                  setImportFile(file);
                  setImportStatus("");
                  setIsError(false);
                } else {
                  setImportStatus("Please upload a valid CSV file");
                  setIsError(true);
                }
              }
            }}
          >
            <div className="upload-icon">
              <Upload size={16} />
            </div>
            <div className="upload-text">
              <span>Click to upload</span> or drag and drop
              <p className="file-types">CSV (max 10MB)</p>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="file-input"
              accept=".csv"
              onChange={handleFileChange}
            />
          </div>

          {importFile && (
            <div className="file-preview">
              <div className="file-info">
                <FileText size={14} className="text-primary" />
                <span>{importFile.name}</span>
                <button
                  className="remove-file"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImportFile(null);
                    setImportStatus("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="file-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleImport}
                  disabled={!importFile}
                >
                  Import
                </button>
              </div>
            </div>
          )}

          {importStatus && (
            <div
              className={`import-status ${isError ? "error" : "success"} mt-2`}
            >
              {importStatus}
            </div>
          )}
        </div>

        <div className="file-format-info small">
          <p className="mb-1">CSV format:</p>
          <div className="format-example">
            <div className="format-header">
              <span>root</span>
              <span>synonyms</span>
            </div>
            <div className="format-row">
              <span>iPhone</span>
              <span>iphone|apple phone</span>
            </div>
            <div className="format-row">
              <span>Laptop</span>
              <span>laptop|notebook|computer</span>
            </div>
          </div>
          <div className="text-end mt-2">
            <a
              href="#"
              className="text-primary text-decoration-none"
              onClick={(e) => {
                e.preventDefault();
                const csvContent = "root,synonyms\niPhone,iphone|apple phone";
                const blob = new Blob([csvContent], {
                  type: "text/csv;charset=utf-8;",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "synonyms_template.csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download Template
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return <div className="export-import">{renderImportSection()}</div>;
};

export default ExportImport;
